import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BibleVerse from "@/components/BibleVerse";
import { translateScriptureReference } from "@/utils/bibleTranslations";

interface YouTubeVideo {
  id: string;
  title: string;
  youtube_url: string;
  description: string | null;
}

interface VideoTranslation {
  title: string;
  description: string | null;
}

const getYouTubeVideoId = (url: string): string => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : '';
};

export default function LatestYouTubeVideo() {
  const { t, language } = useLanguage();
  const [latestVideo, setLatestVideo] = useState<YouTubeVideo | null>(null);
  const [translation, setTranslation] = useState<VideoTranslation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLatestVideo();
  }, [language]);

  const fetchLatestVideo = async () => {
    try {
      const { data, error } = await supabase
        .from("youtube_videos")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setLatestVideo(data);

      // Fetch translation for the current language if video exists
      if (data) {
        const { data: translationData } = await supabase
          .from("youtube_videos_translations")
          .select("title, description")
          .eq("video_id", data.id)
          .eq("language", language)
          .single();

        setTranslation(translationData);
      }
    } catch (error) {
      console.error("Error fetching latest video:", error);
    } finally {
      setLoading(false);
    }
  };

  const parseScriptureReferences = (description: string | null): string[] => {
    if (!description) return [];
    // Split by comma, semicolon, or newline, trim whitespace, remove bullet points and filter empty strings
    const refs = description.split(/[,;\n]/).map(ref => 
      ref.trim().replace(/^[•\-\*]\s*/, '') // Remove leading bullets, dashes, asterisks
    ).filter(ref => ref.length > 0);
    // Translate each reference to the current language
    return refs.map(ref => translateScriptureReference(ref, language));
  };

  const getVideoContent = () => {
    return {
      title: translation?.title || latestVideo?.title || "",
      description: translation?.description || latestVideo?.description
    };
  };

  if (loading || !latestVideo) return null;

  const content = getVideoContent();

  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">{t("youtube.latest.title")}</h2>
            <p className="text-muted-foreground">{t("youtube.latest.subtitle")}</p>
          </div>

          <div className="bg-card rounded-lg shadow-lg overflow-hidden">
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(latestVideo.youtube_url)}`}
                title={content.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold mb-2">{content.title}</h3>
              {content.description && (
                <div className="mb-4 space-y-2" onClick={(e) => e.stopPropagation()}>
                  {parseScriptureReferences(content.description).map((ref, idx) => (
                    <BibleVerse key={idx} reference={ref} compact />
                  ))}
                </div>
              )}
              <Link to="/youtube-videos">
                <Button>
                  <Play className="w-4 h-4 mr-2" />
                  {t("youtube.latest.viewall")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
